/**
 * ModArcController — the bridge between the Mod Matrix (router/connections)
 * and the Knob overlay that draws one colored arc per active modulation.
 *
 * Knobs need to know: "which connections target me, and with what depth?"
 * The router has that data, but knobs are created at startup while the
 * router is instantiated lazily on first audio unlock. This controller
 * soaks up that timing: it's created eagerly, handed to every Knob, and
 * gets its router reference later via `setRouter()`. Until then, every
 * arc query returns empty — knobs render without overlays.
 *
 * Source coloring is done by source-id convention (macro-*, velocity,
 * keytrack, and fall-through 'generic'). Callers tag LFO / S&H IDs via
 * `setSourceKind` so the controller can classify them without hard-coding
 * the module ids here.
 */

import { getTargetScale, type ModTarget } from './targets';
import type { ModRouter } from './ModRouter';

/** Visual kind — one CSS color per category. */
export type ModSourceKind = 'lfo' | 'sh' | 'macro' | 'velocity' | 'keytrack' | 'generic';

export interface ModArc {
  readonly sourceId: string;
  readonly kind: ModSourceKind;
  /** User-facing depth (−1..+1); sign indicates arc direction. */
  readonly depth: number;
  /** Scale from the target's ModTarget entry. Depth × scale = mod amount in native units. */
  readonly modScale: number;
}

export type ModArcListener = () => void;

export class ModArcController {
  private router: ModRouter | null = null;
  private targets: readonly ModTarget[] = [];
  private unsubRouter: (() => void) | null = null;
  private readonly listeners = new Set<ModArcListener>();
  private readonly sourceKinds = new Map<string, ModSourceKind>();

  /**
   * Install the live router. Called once after audio unlock finishes. If
   * called again (e.g., router recreation), the previous subscription is
   * torn down cleanly.
   */
  setRouter(router: ModRouter, targets: readonly ModTarget[]): void {
    if (this.unsubRouter) this.unsubRouter();
    this.router = router;
    this.targets = targets;
    this.unsubRouter = router.subscribe(() => this.emit());
    this.emit();
  }

  /**
   * Swap in a fresh target list — used when the effects chain mutates
   * (add / remove / rename) and target modScales need to follow. The
   * router is left alone; only the controller's view of targets is
   * updated. Fires one change event so knob arcs repaint.
   */
  setTargets(targets: readonly ModTarget[]): void {
    this.targets = targets;
    this.emit();
  }

  /** Tag a registered source with a kind so arcs pick the right color. */
  setSourceKind(sourceId: string, kind: ModSourceKind): void {
    this.sourceKinds.set(sourceId, kind);
  }

  /** Arcs for the knob at (moduleId, paramName). Empty when no router is set. */
  arcsFor(moduleId: string, paramName: string): ModArc[] {
    if (!this.router) return [];
    const modScale = getTargetScale(this.targets, moduleId, paramName);
    const out: ModArc[] = [];
    for (const c of this.router.listConnections()) {
      if (c.targetModuleId !== moduleId || c.targetParam !== paramName) continue;
      out.push({
        sourceId: c.sourceModuleId,
        kind: this.kindOf(c.sourceModuleId),
        depth: c.depth,
        modScale,
      });
    }
    return out;
  }

  subscribe(fn: ModArcListener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  dispose(): void {
    if (this.unsubRouter) this.unsubRouter();
    this.unsubRouter = null;
    this.router = null;
    this.listeners.clear();
  }

  private kindOf(sourceId: string): ModSourceKind {
    // Per-note sentinels are well-known. Macros follow a prefix convention.
    // Everything else looks up its tag; default falls through to generic.
    if (sourceId === 'velocity') return 'velocity';
    if (sourceId === 'keytrack') return 'keytrack';
    if (sourceId.startsWith('macro-')) return 'macro';
    return this.sourceKinds.get(sourceId) ?? 'generic';
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }
}
